import { LoginType, SocialType } from "../../_genApi/gameDeploy";
import { loginWithGoogle } from "./login-helpers/google-login-helper";
import Network from "../networkHandler/Network";
import AbstractAuthModule from "./AbstractAuthModule";
import { login, getMe } from "./auth-api";
import { getDeviceId } from '../device-id';

let isLoggingIn: boolean = false;

export default class SocialAuthModule extends AbstractAuthModule {
	loginType = LoginType.SOCIAL;

	constructor() {
		super();
	}

	start() {
		let prevAuthCre = this.readCachedUserInfo();
		if (!prevAuthCre) return;
		if (!prevAuthCre.zpsToken) return;

		// Restore session — set zpsToken on network handlers
		Network.withServerUploadGame().setToken(prevAuthCre.zpsToken);
		Network.withServerAssistant().setToken(prevAuthCre.zpsToken);
		this.setCurrentUser(prevAuthCre);
		this.onLoggedIn();

		// Validate token in background — logout if expired (401)
		const serverUrl = Network.withServerUploadGame().getServerUrl();
		getMe(serverUrl, prevAuthCre.zpsToken).then(user => {
			if (!user) {
				console.warn('ZPS token expired or invalid, clearing session');
				this.logout();
			}
		});
	}

	async userReLogin(socialType: SocialType) {
		console.log('SocialAuthModule userReLogin', socialType);

		// Reset any stuck flag from a previous cancelled/dismissed popup.
		// The GIS callback is not guaranteed to fire when the user closes the
		// Google OAuth popup, so `isLoggingIn` can remain true indefinitely.
		isLoggingIn = false;

		this.logout();
		isLoggingIn = true;
		try {
			switch (socialType) {
				case SocialType.GOOGLE:
					await this._loginWithGoogle();
					break;
				default:
					console.error('FATAL in userReLogin: Unknown socialType', socialType);
			}
		} catch (e) {
			console.error('FATAL in userReLogin: login error with socialType', socialType, e);
		} finally {
			isLoggingIn = false;
		}
	}

	/**
	 * Google popup login:
	 * 1. GIS popup → Google access token
	 * 2. POST /api/get-zps-token → ZPS JWT
	 * 3. GET /api/me → user info
	 */
	private async _loginWithGoogle() {
		// Step 1: Google popup → access token
		const googleAccessToken = await loginWithGoogle();

		// Step 2: Exchange for ZPS token + get user info
		const serverUrl = Network.withServerUploadGame().getServerUrl();
		const deviceId = getDeviceId();
		const { user, token: zpsToken } = await login(serverUrl, {
			accessToken: googleAccessToken,
			deviceId,
			socialType: SocialType.GOOGLE,
		});

		// Step 3: Set token on network handlers
		Network.withServerUploadGame().setToken(zpsToken);
		Network.withServerAssistant().setToken(zpsToken);

		// Step 4: Store user info
		this.setCurrentUser({
			loginType: LoginType.SOCIAL,
			socialType: SocialType.GOOGLE,
			zpsToken,
			zpsUserId: user.uid,
			zpsUsername: user.username,
			name: user.fullName,
			avatar: user.avatar,
		});

		await this.onLoggedIn();
	}

	getLoginToken() {
		return this.getCurrentUser()?.zpsToken;
	}

	getUsername() {
		return this.getCurrentUser()?.name;
	}

	hadLoggedIn() {
		let prevAuthCre = this.readCachedUserInfo();
		if (!prevAuthCre) return false;
		return !!prevAuthCre.zpsToken;
	}

	/** @deprecated Use userReLogin instead — kept for GoogleLoginRedirectGetAuthenCodePage compat */
	async loadToken(socialType: SocialType, authenCode: string) {
		console.warn('loadToken is deprecated — popup flow no longer uses auth codes');
	}
}
